import { calculatePages } from "./calculatePages.js";
import { getNamespacedResource } from "SharedStores/util";
import { convertValueToString } from "Util/util";
import { idGenerator, uriGenerator } from "Util/uniqueIdGenerator";
import { getNewListItem, getListItemKey } from "Forms/EditForm/clientCreatable.js";
import { ELEMENT_PROPERTY_NAME } from "Types/elementTypes";

//getters
const ORDERED_FORM_ELEMENTS = "orderedFormElements";
const CALCULATE_FORM_PAGES = "calculateFormPages";
const ELEM_FROM_ID = "elementFromId";
const FORM_NAMESPACE = "form";
const COUNTIES_FROM_STATE_SHORTNAME = "countiesFromStateShortName";
const US_STATES = "USStates";
const CA_PROVINCES = "CAProvinces";
const COUNTRY_FROM_STATE = "countryFromState";
const DEPENDENCY_BY_LABEL = "dependencyByLabel";
const DATA_FIELD_IDS_ON_FORM = "dataFieldIdsOnForm";

//mutations
const TITLE = "Title";
const SUBTITLE = "Subtitle";
const DESCRIPTION = "Description";
const ADD_ELEMENT = "addElement";
const ADD_ELEMENT_ORDER = "addElementOrder";
const MOVE_ELEMENTS = "moveElements";
const DELETE_ELEMENT = "deleteElement";
const ELEMENT_PROP = "elementProperty";
const DELETE_ELEMENT_PROP = "deleteElementProperty";
const ADD_EMPTY_VIS_RULE = "addEmptyVisibilityRule";
const DELETE_VIS_RULE = "deleteVisibilityRule";
const VIS_RULE = "visibilityRule";
const ADD_EMPTY_LIST_ITEM = "addEmptyListItem";
const COMMIT_LIST_VIEW_ITEM = "commitListViewItem";
const COMMIT_LIST_VIEW_ORDER = "commitListViewOrder";
const ADD_EMPTY_DATA_DEPENDENCY = "addEmptyDataDependency";
const DELETE_DATA_DEPENDENCY = "deleteDataDependency";
const COMMIT_DATA_DEPENDENCY = "commitDataDependency";
const COMMIT_DATA_DEPENDENCY_ORDER = "commitDataDependencyOrder";
const ADD_EMPTY_SIGNATORY = "addEmptySignatory";
const DELETE_SIGNATORY = "deleteSignatory";
const COMMIT_SIGNATORY_DATA = "commitSignatoryData";
const CLEAR_SIGNATORY_LIST = "clearSignatoryList";
const COMMIT_AUXILIARY_MODEL = "commitAuxiliaryModel";
const COMMIT_DATA_FIELD_ID = "commitDataFieldId";
const COMMIT_DATA_FIELD_GROUP_ID = "commitDataFieldGroupId";
const COMMIT_ATTACHMENT_ID = "commitAttachmentId";
const COMMIT_GROUP_DATA_FIELDS = "commitGroupDataFields";
const COMMIT_GENERATED_DOCUMENT_TEMPLATE = "commitGeneratedDocumentTemplate";
const COMMIT_ESIGNATURE_CONSTANT = "commitESignatureConstant";
const COMMIT_ENVELOPE_ID = "commitEnvelopeId";

const FORM_TYPES = {
    getters: {
        [ORDERED_FORM_ELEMENTS]: getNamespacedResource(FORM_NAMESPACE, ORDERED_FORM_ELEMENTS),
        [CALCULATE_FORM_PAGES]: getNamespacedResource(FORM_NAMESPACE, CALCULATE_FORM_PAGES),
        [ELEM_FROM_ID]: getNamespacedResource(FORM_NAMESPACE, ELEM_FROM_ID),
        [COUNTIES_FROM_STATE_SHORTNAME]: getNamespacedResource(FORM_NAMESPACE, COUNTIES_FROM_STATE_SHORTNAME),
        [US_STATES]: getNamespacedResource(FORM_NAMESPACE, US_STATES),
        [CA_PROVINCES]: getNamespacedResource(FORM_NAMESPACE, CA_PROVINCES),
        [COUNTRY_FROM_STATE]: getNamespacedResource(FORM_NAMESPACE, COUNTRY_FROM_STATE),
        [DEPENDENCY_BY_LABEL]: getNamespacedResource(FORM_NAMESPACE, DEPENDENCY_BY_LABEL),
        [DATA_FIELD_IDS_ON_FORM]: getNamespacedResource(FORM_NAMESPACE, DATA_FIELD_IDS_ON_FORM)
    },
    mutations: {
        [TITLE]: getNamespacedResource(FORM_NAMESPACE, TITLE),
        [SUBTITLE]: getNamespacedResource(FORM_NAMESPACE, SUBTITLE),
        [DESCRIPTION]: getNamespacedResource(FORM_NAMESPACE, DESCRIPTION),
        [ADD_ELEMENT]: getNamespacedResource(FORM_NAMESPACE, ADD_ELEMENT),
        [ADD_ELEMENT_ORDER]: getNamespacedResource(FORM_NAMESPACE, ADD_ELEMENT_ORDER),
        [MOVE_ELEMENTS]: getNamespacedResource(FORM_NAMESPACE, MOVE_ELEMENTS),
        [DELETE_ELEMENT]: getNamespacedResource(FORM_NAMESPACE, DELETE_ELEMENT),
        [ELEMENT_PROP]: getNamespacedResource(FORM_NAMESPACE, ELEMENT_PROP),
        [DELETE_ELEMENT_PROP]: getNamespacedResource(FORM_NAMESPACE, DELETE_ELEMENT_PROP),
        [ADD_EMPTY_VIS_RULE]: getNamespacedResource(FORM_NAMESPACE, ADD_EMPTY_VIS_RULE),
        [DELETE_VIS_RULE]: getNamespacedResource(FORM_NAMESPACE, DELETE_VIS_RULE),
        [VIS_RULE]: getNamespacedResource(FORM_NAMESPACE, VIS_RULE),
        [ADD_EMPTY_LIST_ITEM]: getNamespacedResource(FORM_NAMESPACE, ADD_EMPTY_LIST_ITEM),
        [COMMIT_LIST_VIEW_ITEM]: getNamespacedResource(FORM_NAMESPACE, COMMIT_LIST_VIEW_ITEM),
        [COMMIT_LIST_VIEW_ORDER]: getNamespacedResource(FORM_NAMESPACE, COMMIT_LIST_VIEW_ORDER),
        [ADD_EMPTY_DATA_DEPENDENCY]: getNamespacedResource(FORM_NAMESPACE, ADD_EMPTY_DATA_DEPENDENCY),
        [DELETE_DATA_DEPENDENCY]: getNamespacedResource(FORM_NAMESPACE, DELETE_DATA_DEPENDENCY),
        [COMMIT_DATA_DEPENDENCY]: getNamespacedResource(FORM_NAMESPACE, COMMIT_DATA_DEPENDENCY),
        [COMMIT_DATA_DEPENDENCY_ORDER]: getNamespacedResource(FORM_NAMESPACE, COMMIT_DATA_DEPENDENCY_ORDER),
        [ADD_EMPTY_SIGNATORY]: getNamespacedResource(FORM_NAMESPACE, ADD_EMPTY_SIGNATORY),
        [DELETE_SIGNATORY]: getNamespacedResource(FORM_NAMESPACE, DELETE_SIGNATORY),
        [COMMIT_SIGNATORY_DATA]: getNamespacedResource(FORM_NAMESPACE, COMMIT_SIGNATORY_DATA),
        [CLEAR_SIGNATORY_LIST]: getNamespacedResource(FORM_NAMESPACE, CLEAR_SIGNATORY_LIST),
        [COMMIT_AUXILIARY_MODEL]: getNamespacedResource(FORM_NAMESPACE, COMMIT_AUXILIARY_MODEL),
        [COMMIT_DATA_FIELD_ID]: getNamespacedResource(FORM_NAMESPACE, COMMIT_DATA_FIELD_ID),
        [COMMIT_DATA_FIELD_GROUP_ID]: getNamespacedResource(FORM_NAMESPACE, COMMIT_DATA_FIELD_GROUP_ID),
        [COMMIT_ATTACHMENT_ID]: getNamespacedResource(FORM_NAMESPACE, COMMIT_ATTACHMENT_ID),
        [COMMIT_GROUP_DATA_FIELDS]: getNamespacedResource(FORM_NAMESPACE, COMMIT_GROUP_DATA_FIELDS),
        [COMMIT_GENERATED_DOCUMENT_TEMPLATE]: getNamespacedResource(FORM_NAMESPACE, COMMIT_GENERATED_DOCUMENT_TEMPLATE),
        [COMMIT_ESIGNATURE_CONSTANT]: getNamespacedResource(FORM_NAMESPACE, COMMIT_ESIGNATURE_CONSTANT),
        [COMMIT_ENVELOPE_ID]: getNamespacedResource(FORM_NAMESPACE, COMMIT_ENVELOPE_ID)
    }
};

const ITEM_ID_GENERATOR = new idGenerator();
const ITEM_URI_GENERATOR = new uriGenerator();

/**
 * Gathers any additional data fields the element may be responsible for.
 * Method can be expanded as new cases are introduced.
 * *
 * @param {Object} element
 * @returns {Array}
 */
function getElementAdditionalResponsibleFields(element) {
    const additionalDataFieldIds = [];
    // contact and address elements may contain location sub element
    if (element.ClassName == "ContactElement" || element.ClassName == "AddressElement") {
        if (ELEMENT_PROPERTY_NAME.LocationSubElementId in element.Properties) {
            additionalDataFieldIds.push(element.Properties[ELEMENT_PROPERTY_NAME.LocationSubElementId]);
        }
    }
    return additionalDataFieldIds;
}

export { FORM_NAMESPACE, FORM_TYPES };

export function createFormModule(state) {
    const usCode = 1;
    const caCode = 2;

    return {
        namespaced: true,
        state: state,
        getters: {
            [ORDERED_FORM_ELEMENTS](state) {
                return state.OrderedFormElementIds.map((id) => state.FormElements[id]);
            },
            [CALCULATE_FORM_PAGES](state, getters) {
                return function ({ forceOnePage }) {
                    return calculatePages(getters.orderedFormElements, true, forceOnePage);
                };
            },
            [ELEM_FROM_ID](state) {
                return function (elementId) {
                    return state.FormElements[elementId];
                };
            },
            [US_STATES](state) {
                const states = state.StateDefinitions.filter((sd) => sd.CountryConstantId === usCode);
                states.sort((a, b) => a.ShortName.localeCompare(b.ShortName));
                return states;
            },
            [CA_PROVINCES](state) {
                const provinces = state.StateDefinitions.filter((sd) => sd.CountryConstantId === caCode);
                provinces.sort((a, b) => a.ShortName.localeCompare(b.ShortName));
                return provinces;
            },
            [COUNTRY_FROM_STATE](state) {
                return function (stateShortName) {
                    const selectedState = state.StateDefinitions.find((sd) => sd.ShortName === stateShortName);
                    if (selectedState) {
                        return selectedState.CountryConstantId;
                    } else {
                        return 0;
                    }
                };
            },

            [COUNTIES_FROM_STATE_SHORTNAME](state) {
                const unsortedCountiesByStateId = {};
                const countiesByState = {};
                state.CountyDefinitions.forEach((cd) => {
                    if (!unsortedCountiesByStateId.hasOwnProperty(cd.StateDefinitionId)) {
                        unsortedCountiesByStateId[cd.StateDefinitionId] = [];
                    }
                    unsortedCountiesByStateId[cd.StateDefinitionId].push(cd);
                });
                state.StateDefinitions.forEach((sd) => {
                    if (unsortedCountiesByStateId.hasOwnProperty(sd.StateDefinitionId)) {
                        countiesByState[sd.ShortName] = unsortedCountiesByStateId[sd.StateDefinitionId].sort((a, b) =>
                            a.CountyName.localeCompare(b.CountyName)
                        );
                    }
                });
                return function (stateShortName) {
                    const stateDef = state.StateDefinitions.find((sd) => sd.ShortName === stateShortName);
                    if (stateDef && countiesByState.hasOwnProperty(stateShortName)) {
                        return countiesByState[stateShortName];
                    }
                    return null;
                };
            },
            [DEPENDENCY_BY_LABEL](state) {
                return function (elementId, label) {
                    const element = state.FormElements[elementId];
                    for (let index = 0; index < element.DependencyDefinitions.length; index++) {
                        if (element.DependencyDefinitions[index].Label === label) {
                            return element.DependencyDefinitions[index];
                        }
                    }
                    return null;
                };
            },
            [DATA_FIELD_IDS_ON_FORM](state) {
                let dataFieldIds = [];
                for (const element of Object.values(state.FormElements)) {
                    if (element.DataFieldId) {
                        dataFieldIds.push(element.DataFieldId);
                    }
                    if (element.GroupDataFields) {
                        dataFieldIds = dataFieldIds.concat(Object.values(element.GroupDataFields));
                    }

                    const additionalDataFieldIds = getElementAdditionalResponsibleFields(element);
                    if (additionalDataFieldIds.length > 0) {
                        dataFieldIds = dataFieldIds.concat(additionalDataFieldIds);
                    }
                    //
                }
                return dataFieldIds;
            }
        },
        mutations: {
            [TITLE](state, { title }) {
                state.Title = title;
            },
            [SUBTITLE](state, { subtitle }) {
                state.Subtitle = subtitle;
            },
            [DESCRIPTION](state, { description }) {
                state.Description = description;
            },
            [ADD_ELEMENT](state, { element, index }) {
                state.FormElements[element.PublicId] = element;
                if (Number.isInteger(index)) {
                    state.OrderedFormElementIds.splice(index, 0, element.PublicId);
                }
            },
            [ADD_ELEMENT_ORDER](state, { elementId, index }) {
                state.OrderedFormElementIds.splice(index, 0, elementId);
            },
            [MOVE_ELEMENTS](state, { targetIndex, sortedKeys }) {
                for (const id of sortedKeys) {
                    const index = state.OrderedFormElementIds.indexOf(id);
                    if (index >= 0) {
                        state.OrderedFormElementIds.splice(index, 1);
                    }
                }

                state.OrderedFormElementIds.splice(targetIndex, 0, ...sortedKeys);
            },
            [DELETE_ELEMENT](state, { element }) {
                const elementIndex = state.OrderedFormElementIds.indexOf(element.PublicId);
                if (elementIndex >= 0) {
                    state.OrderedFormElementIds.splice(elementIndex, 1);
                }
                delete state.FormElements[element.PublicId];
            },
            [ELEMENT_PROP](state, { elementId, propertyName, propertyValue }) {
                propertyValue = convertValueToString(propertyValue);
                const element = state.FormElements[elementId];
                element.Properties[propertyName] = propertyValue;
            },
            [DELETE_ELEMENT_PROP](state, { elementId, propertyName }) {
                const element = state.FormElements[elementId];
                delete element.Properties[propertyName];
            },
            [ADD_EMPTY_VIS_RULE](state, { elementId }) {
                const element = state.FormElements[elementId];
                element.VisibilityRule = {
                    DataFieldId: null,
                    Operator: 0,
                    RightOperand: null
                };
            },
            [DELETE_VIS_RULE](state, { elementId }) {
                const element = state.FormElements[elementId];
                element.VisibilityRule = null;
            },
            [VIS_RULE](state, { elementId, rule }) {
                const element = state.FormElements[elementId];
                element.VisibilityRule = rule;
            },
            [ADD_EMPTY_LIST_ITEM](state, { elementId, index }) {
                /**
                 * Elements that derive presentation from ListView properties (e.g. dropdown)
                 * bind to and update the AuxiliaryModel during editing. This is done to simplify
                 * reconciliation during save.
                 */
                const element = state.FormElements[elementId];

                const placeholderId = ITEM_URI_GENERATOR.getUnique((uri) =>
                    element.AuxiliaryModel.ListView.find((li) => li.PlaceholderId === uri)
                );
                const item = getNewListItem("", "", index, placeholderId);
                element.AuxiliaryModel.ListView.splice(index, 0, item);

                // Update list item order
                for (let updateIndex = index + 1; updateIndex < element.AuxiliaryModel.ListView.length; updateIndex++) {
                    element.AuxiliaryModel.ListView[updateIndex].Order = updateIndex;
                }
            },
            [COMMIT_LIST_VIEW_ITEM](state, { elementId, index, item }) {
                const element = state.FormElements[elementId];
                if (element.AuxiliaryModel.ListView && element.AuxiliaryModel.ListView[index]) {
                    const currentItem = element.AuxiliaryModel.ListView[index];
                    currentItem.Name = item.Name;
                    currentItem.Label = item.Label;
                    currentItem.Order = item.Order;
                }
            },
            [COMMIT_LIST_VIEW_ORDER](state, { elementId, listViewIds }) {
                const element = state.FormElements[elementId];
                const indexMap = {};
                listViewIds.forEach((id, index) => {
                    indexMap[id] = index;
                });
                if (element.AuxiliaryModel.ListView) {
                    const orderedListView = [];
                    const isRemoving = listViewIds.length < element.AuxiliaryModel.ListView.length;
                    element.AuxiliaryModel.ListView.forEach((listItem) => {
                        const listItemKey = getListItemKey(listItem);
                        if (indexMap.hasOwnProperty(listItemKey)) {
                            listItem.Order = indexMap[listItemKey];
                            orderedListView.push(listItem);

                            if (isRemoving) {
                                /**
                                 * We've seen some cases where the list view is corrupt and two items have the same list view id. In that case, allow the user
                                 * to delete all but one. In the typical case (only one of each item), this won't do anything relevant.
                                 */
                                delete indexMap[listItemKey];
                            }
                        }
                    });

                    element.AuxiliaryModel.ListView = orderedListView.sort((a, b) => a.Order - b.Order);
                }
            },
            [ADD_EMPTY_DATA_DEPENDENCY](state, { elementId, index }) {
                const element = state.FormElements[elementId];

                const dependency = {
                    PublicId: ITEM_ID_GENERATOR.getNextId("client_data_dependency_id_"),
                    DataFieldId: "",
                    IsConditional: false,
                    Label: "",
                    IsNew: true
                };
                element.DependencyDefinitions.splice(index, 0, dependency);
            },
            [DELETE_DATA_DEPENDENCY](state, { elementId, index }) {
                const element = state.FormElements[elementId];
                if (element.DependencyDefinitions) {
                    element.DependencyDefinitions.splice(index, 1);
                }
            },
            [COMMIT_DATA_DEPENDENCY](state, { elementId, index, dataDependency }) {
                const element = state.FormElements[elementId];
                if (element.DependencyDefinitions && element.DependencyDefinitions[index]) {
                    const currentDataDependency = element.DependencyDefinitions[index];
                    currentDataDependency.DataFieldId = dataDependency.DataFieldId;
                    currentDataDependency.IsConditional = dataDependency.IsConditional;
                    currentDataDependency.Label = dataDependency.Label;
                }
            },
            [COMMIT_DATA_DEPENDENCY_ORDER](state, { elementId, dataDependencyIds }) {
                const element = state.FormElements[elementId];
                const indexMap = {};
                dataDependencyIds.forEach((id, index) => {
                    indexMap[id] = index;
                });
                if (element.DependencyDefinitions) {
                    const orderedDependencies = [];
                    element.DependencyDefinitions.forEach((dd) => {
                        if (indexMap.hasOwnProperty(dd.PublicId)) {
                            orderedDependencies.push(dd);
                        }
                    });

                    element.DependencyDefinitions = orderedDependencies.sort(
                        (a, b) => indexMap[a.PublicId] - indexMap[b.PublicId]
                    );
                }
            },
            [ADD_EMPTY_SIGNATORY](state, { elementId, index }) {
                const element = state.FormElements[elementId];

                const signatory = {
                    EmailDataFieldId: "",
                    FirstNameDataFieldId: "",
                    Label: "",
                    LastNameDataFieldId: "",
                    IsNew: true
                };
                if (!element.Signatories) {
                    element.Signatories = [];
                }
                element.Signatories.splice(index, 0, signatory);
            },
            [DELETE_SIGNATORY](state, { elementId, index }) {
                const element = state.FormElements[elementId];
                if (element.Signatories) {
                    element.Signatories.splice(index, 1);
                }
            },
            [COMMIT_SIGNATORY_DATA](state, { elementId, index, signatory }) {
                const element = state.FormElements[elementId];
                if (element.Signatories && element.Signatories[index]) {
                    const existingSignatory = element.Signatories[index];
                    existingSignatory.EmailDataFieldId = signatory.EmailDataFieldId;
                    existingSignatory.FirstNameDataFieldId = signatory.FirstNameDataFieldId;
                    existingSignatory.Label = signatory.Label;
                    existingSignatory.LastNameDataFieldId = signatory.LastNameDataFieldId;
                }
            },
            [CLEAR_SIGNATORY_LIST](state, { elementId }) {
                const element = state.FormElements[elementId];
                element.Signatories = null;
            },
            [COMMIT_AUXILIARY_MODEL](state, { elementId, auxiliaryModel }) {
                const element = state.FormElements[elementId];
                element.AuxiliaryModel = auxiliaryModel;
            },
            [COMMIT_DATA_FIELD_ID](state, { elementId, dataFieldId }) {
                const element = state.FormElements[elementId];
                element.DataFieldId = dataFieldId;
            },
            [COMMIT_DATA_FIELD_GROUP_ID](state, { elementId, dataFieldGroupId }) {
                const element = state.FormElements[elementId];
                element.DataFieldGroupId = dataFieldGroupId;
            },
            [COMMIT_ATTACHMENT_ID](state, { elementId, attachmentId }) {
                const element = state.FormElements[elementId];
                element.AttachmentId = attachmentId;
            },
            [COMMIT_GROUP_DATA_FIELDS](state, { elementId, groupedDataFields }) {
                const element = state.FormElements[elementId];
                element.GroupDataFields = groupedDataFields;
            },
            [COMMIT_GENERATED_DOCUMENT_TEMPLATE](state, { elementId, generatedDocumentTemplateId }) {
                const element = state.FormElements[elementId];
                element.GeneratedDocumentTemplateId = generatedDocumentTemplateId;
            },
            [COMMIT_ESIGNATURE_CONSTANT](state, { elementId, eSignatureConstant }) {
                const element = state.FormElements[elementId];
                element.ESignatureConstant = eSignatureConstant;
            },
            [COMMIT_ENVELOPE_ID](state, { elementId, envelopeId }) {
                const element = state.FormElements[elementId];
                element.EnvelopeId = envelopeId;
            }
        }
    };
}
